import { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- To be migrated to useAtlassianAccountTraits. See ACTIVATION-2527
import { useExperimentalUserPersonalizationQuery } from '../useExperimentalUserPersonalizationQuery.ts';
import { CONTEXTUAL_INVITE_NUDGE } from '../../constants/onboarding-state/onboarding-contextual-invite-nudge.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '../../constants/onboarding-state/onboarding-editor-constants.ts';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '../onboardingState/onboardingState.ts';
import { useIsEstablishedUserTraitName } from '../useIsEstablishedUserTraitName.ts';
import { memoizedGetInvitePermissions } from '../../utils/getInvitePermissions.ts';
import { LIVE_PAGE_NUDGE } from '../../constants/onboarding-state/onboarding-live-docs-constants.ts';

const CONFLUENCE_KEY = 'confluence';

/**
 * Determines eligibility for the Contextual Invite Nudge experiment
 * @returns {object} - an object containing:
 * @returns {boolean} contextualInviteNudgeEligibleTag- Determines whether the user is eligible for the contextual invite nudge
 * @returns {object} onboardingState - Relevant onboarding state data
 */
export const useContextualInviteNudge = (): {
	contextualInviteNudgeEligibleTag: boolean;
	onboardingState: any;
} => {
	const [isEligibleTag, setIsEligibleTag] = useState(false);

	// Contexts and hooks
	const { userId, cloudId } = useSessionData();
	const isLicensed = useIsLicensed();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	// Onboarding state
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
			CONTEXTUAL_INVITE_NUDGE.WAS_CONFETTI_SEEN,
			EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
			LIVE_PAGE_NUDGE.LIVE_PAGE_CONTEXTUAL_NUDGE_SEEN,
		],
		!fg('cc_onboarding_experience'),
	);

	const onboardingState = deserializeState(onboardingStateData);
	const { contextualInviteNudgeEligibleTag, hasUserPublished, wasLivePageNudgeSeen } =
		onboardingState;

	const skip =
		!isLicensed ||
		onboardingStateLoading ||
		Boolean(contextualInviteNudgeEligibleTag) ||
		Boolean(hasUserPublished) ||
		Boolean(wasLivePageNudgeSeen) ||
		!fg('cc_onboarding_experience');

	// User personalization query for isEstablishedUser
	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useExperimentalUserPersonalizationQuery({
		variables: {
			userId: userId ?? '',
		},
		skip,
	});

	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];
	const isEstablishedUser =
		attributes.find((attr) => attr?.name === isEstablishedUserTraitName)?.value === 'true';

	// Combined loading and error states
	const loading = userPersonalizationQueryLoading || onboardingStateLoading;
	const error = userPersonalizationQueryError || onboardingStateError;
	useEffect(() => {
		if (skip || loading || error || isEstablishedUser) {
			return;
		}

		const fetchInvitePermsAndSetEligibility = async () => {
			const response = await memoizedGetInvitePermissions(cloudId);
			if (response instanceof Error) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'editorOrViewPage',
						action: 'failed',
						actionSubject: 'getInvitePermissions',
						attributes: {
							message: response?.message,
						},
					},
				}).fire();
			}

			const canInviteToConfluence: boolean = [
				...(response?.directInvite ?? []),
				...(response?.invitePendingApproval ?? []),
			].some((item) => item.includes(CONFLUENCE_KEY));

			if (canInviteToConfluence) {
				void setOnboardingState({
					key: CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
					value: 'true',
				});
				setIsEligibleTag(true);
			}
		};
		void fetchInvitePermsAndSetEligibility();
	}, [
		error,
		loading,
		skip,
		cloudId,
		contextualInviteNudgeEligibleTag,
		isEstablishedUser,
		setOnboardingState,
		createAnalyticsEvent,
	]);

	return {
		contextualInviteNudgeEligibleTag: Boolean(isEligibleTag || contextualInviteNudgeEligibleTag),
		onboardingState,
	};
};
